/**
 * **画布上的指针交互** —— 我们自己的状态机（原来是 `d3-zoom` + `d3-drag` +
 * Vue Flow 的 `selection` / `nodeDrag` 那几套）。
 *
 * ## 为什么用"纯 reduce + 薄接线"这个形状
 *
 * 和 `canvasGesture.ts`（连线手势）同一个路子，理由也一样：
 *
 * ```text
 *   纯函数：  事件 → 新状态 + 要说的话（命令）
 *   接线层：  把 DOM 事件喂进去，把命令执行掉（改文档 / 发事件）
 * ```
 *
 * 画布交互**大部分是坐标数学**（框选要判矩形相交、拖动要算位移、
 * 双击要判落点），而坐标数学在 happy-dom 里**没有布局可验** ——
 * 纯函数是唯一能被真正测住的部分（`verify:canvas` 里有 20 多条）。
 *
 * ## 状态机管什么
 *
 * | 手势 | 起手 | 过程中的状态 | 松手时的命令 |
 * |---|---|---|---|
 * | 拖节点 | 按在节点上并移动超过阈值 | `dragging`（累计位移） | `move-nodes` |
 * | 框选 | 按在空白处并移动 | `selecting`（矩形） | `select-in-box` |
 * | 平移画布 | 中键（或宿主打开 `panMode`） | `panning` | `pan`（连续） |
 * | 点选 | 按下即抬起（没超过阈值） | `pressing` | `click` |
 *
 * ⚠️ **阈值是必须的**（`DRAG_THRESHOLD`）：没有它，"点一下选中"和
 * "拖一点点"分不开 —— 实测的那种"我想点选，结果节点挪了 1px"就是这样来的
 * （而且那 1px 会被记进历史）。
 *
 * ⚠️ **框选方向无所谓**：从右下往左上拉的框和反过来是同一个框，
 * 所以矩形一律先 `normalizeRect` 再判定（第一次写就踩了：反向拉框选不中）。
 */
import type { Point } from '../../types/geometry'
import type { Rect } from '../frame'

/** 移动超过它才算"拖"，否则算"点"（屏幕像素） */
export const DRAG_THRESHOLD = 3

export interface InteractionModifiers {
  /** 多选键（Meta / Ctrl / Shift）—— 加选而不是替换 */
  additive: boolean
  /**
   * 宿主要求"这次左键拖动用来平移画布"（触摸端 / 显式的平移模式）。
   *
   * ⚠️ **不由画布自己从键盘推导** —— 空格归宿主的 `useHotkeys`
   * （它只做"别让页面滚动"），见 `NpCanvas.vue` 里那段说明：
   * 画布监听键盘既没有用武之地，又最容易和快捷键打架。
   */
  panMode: boolean
  /** 中键 */
  middle: boolean
  /** 右键（框选不接管右键，右键是菜单） */
  right: boolean
}

export interface CanvasPointer {
  /** 屏幕坐标（相对画布容器左上角） */
  host: Point
  /** 画布坐标（同一时刻的换算结果，省得每一步都除 zoom） */
  flow: Point
  modifiers: InteractionModifiers
}

export type CanvasPhase = 'idle' | 'pressing' | 'dragging' | 'selecting' | 'panning'

export interface CanvasInteraction {
  phase: CanvasPhase
  /** 起手点（屏幕坐标）—— 判断阈值、算平移增量都用它 */
  origin: Point
  /** 起手点（画布坐标）—— 框选矩形用画布坐标（缩放时它才跟着内容走） */
  originFlow: Point
  /** 上一步（算增量平移用，屏幕坐标） */
  last: Point
  /**
   * 当前指针（**画布坐标**）。
   *
   * ⚠️ 必须是画布坐标而不是屏幕坐标：框选的矩形要在画布坐标系里算，
   * 而两者差一个 `zoom` —— 用屏幕坐标画框，放大到 2× 之后
   * "框住的东西"会比看到的少一半。
   */
  currentFlow: Point
  /** 正在拖的节点 id（`dragging` 时非空） */
  nodeIds: string[]
  /** 拖动开始时各节点的位置快照（**不能累加增量**，见 `canvasGesture` 的同款教训） */
  nodeOrigins: Record<string, Point>
  modifiers: InteractionModifiers
}

export const IDLE_INTERACTION: CanvasInteraction = {
  phase: 'idle',
  origin: { x: 0, y: 0 },
  originFlow: { x: 0, y: 0 },
  last: { x: 0, y: 0 },
  currentFlow: { x: 0, y: 0 },
  nodeIds: [],
  nodeOrigins: {},
  modifiers: { additive: false, panMode: false, middle: false, right: false },
}

/** 画布要说的话（接线层负责执行） */
export type CanvasCommand =
  | { type: 'pan'; dx: number; dy: number }
  | { type: 'move-nodes'; moves: { id: string; position: Point }[] }
  | { type: 'select-in-box'; box: Rect; additive: boolean }
  | { type: 'click'; flow: Point; hit: CanvasHit; modifiers: InteractionModifiers }
  | { type: 'drag-start'; nodeIds: string[] }
  | { type: 'drag-stop' }

/** 落点上有什么（由接线层用 DOM 判定后传进来） */
export type CanvasHit =
  | { kind: 'pane' }
  | { kind: 'node'; id: string }
  | { kind: 'edge'; id: string }
  /** 端口 / 控件 / 菜单 —— 画布**不接管**（连线手势、控件自己处理） */
  | { kind: 'other' }

export interface ReduceOptions {
  /** 画布坐标的矩形相交判定（纯函数，测试里直接给） */
  hitTest?: (box: Rect, flow: Point) => CanvasHit
}

/** 起手：按下 */
export function beginInteraction(
  point: CanvasPointer,
  hit: CanvasHit,
  options: { selectedIds?: readonly string[]; positions?: Readonly<Record<string, Point>> } = {},
): { state: CanvasInteraction; commands: CanvasCommand[] } {
  const { modifiers } = point
  const base: CanvasInteraction = {
    ...IDLE_INTERACTION,
    phase: 'pressing',
    origin: point.host,
    originFlow: point.flow,
    last: point.host,
    currentFlow: point.flow,
    modifiers,
  }

  /* ① 中键 / 宿主打开的平移模式 → 平移画布（任何落点都算） */
  if (modifiers.middle || modifiers.panMode) {
    return { state: { ...base, phase: 'panning' }, commands: [] }
  }

  /* ② 右键 → 交给菜单，画布不动（按下时什么都不做） */
  if (modifiers.right) return { state: base, commands: [] }

  /* ③ 落在节点上 → 先按"点选"处理，越过阈值才变成拖动 */
  if (hit.kind === 'node') {
    const ids = modifiers.additive
      ? [...new Set([...(options.selectedIds ?? []), hit.id])]
      : (options.selectedIds ?? []).includes(hit.id)
        ? [...(options.selectedIds ?? [])]
        : [hit.id]
    const nodeOrigins: Record<string, Point> = {}
    for (const id of ids) {
      const position = options.positions?.[id]
      if (position) nodeOrigins[id] = { ...position }
    }
    return { state: { ...base, nodeIds: ids, nodeOrigins }, commands: [] }
  }

  /* ④ 落在控件 / 端口上 → 画布不接管（连线手势与控件自己处理） */
  if (hit.kind === 'other') return { state: IDLE_INTERACTION, commands: [] }

  /*
   * ⑤ 空白处 / 连线上 → 框选。
   *
   * ⚠️ 落在**连线上**也走框选：连线上的点击由 `click` 命令处理选中，
   * 而拖动仍然是框选（用户拖过一条线时不该拖不动框）。
   */
  return { state: base, commands: [] }
}

/** 过程：移动 */
export function moveInteraction(
  state: CanvasInteraction,
  point: CanvasPointer,
): { state: CanvasInteraction; commands: CanvasCommand[] } {
  if (state.phase === 'idle') return { state, commands: [] }

  const moved = distance(state.origin, point.host)

  if (state.phase === 'panning') {
    const dx = point.host.x - state.last.x
    const dy = point.host.y - state.last.y
    return {
      state: { ...state, last: point.host, currentFlow: point.flow },
      commands: dx === 0 && dy === 0 ? [] : [{ type: 'pan', dx, dy }],
    }
  }

  if (state.phase === 'pressing') {
    /* 没越过阈值：还算"点"，不动任何东西 */
    if (moved < DRAG_THRESHOLD) {
      return { state: { ...state, currentFlow: point.flow }, commands: [] }
    }
    /* 越过阈值 → 定型：按在节点上就拖节点，否则框选 */
    if (state.nodeIds.length > 0) {
      return {
        state: {
          ...state,
          phase: 'dragging',
          currentFlow: point.flow,
          last: point.host,
        },
        commands: [{ type: 'drag-start', nodeIds: state.nodeIds }],
      }
    }
    return {
      state: { ...state, phase: 'selecting', currentFlow: point.flow },
      commands: [],
    }
  }

  if (state.phase === 'dragging') {
    /* ⚠️ 位移从**起手点的快照**算，不是逐帧累加 —— 累加会丢精度、卡顿时漂 */
    const dxFlow = point.flow.x - state.originFlow.x
    const dyFlow = point.flow.y - state.originFlow.y
    const moves = state.nodeIds.map((id) => {
      const origin = state.nodeOrigins[id] ?? { x: 0, y: 0 }
      return { id, position: { x: origin.x + dxFlow, y: origin.y + dyFlow } }
    })
    return {
      state: { ...state, currentFlow: point.flow, last: point.host },
      commands: [{ type: 'move-nodes', moves }],
    }
  }

  /* selecting：只更新矩形（矩形本身由 `selectionRect(state)` 现算） */
  return { state: { ...state, currentFlow: point.flow }, commands: [] }
}

/**
 * 收尾：松手。
 *
 * `hit` 由接线层在**按下时**判定好、松手时原样传进来 ——
 * 因为它知道 DOM（哪个元素在指针下），而这一层只有坐标。
 */
export function endInteraction(
  state: CanvasInteraction,
  point: CanvasPointer,
  hit: CanvasHit = { kind: 'pane' },
): { state: CanvasInteraction; commands: CanvasCommand[] } {
  if (state.phase === 'idle') return { state, commands: [] }

  const commands: CanvasCommand[] = []
  if (state.phase === 'dragging') commands.push({ type: 'drag-stop' })
  if (state.phase === 'selecting') {
    commands.push({
      type: 'select-in-box',
      box: normalizedBox(state, point.flow),
      additive: state.modifiers.additive,
    })
  }
  if (state.phase === 'pressing') {
    /* 没动过 → 就是一次点击（选中 / 清空选中由接线层按 hit 决定） */
    commands.push({
      type: 'click',
      flow: point.flow,
      hit,
      modifiers: state.modifiers,
    })
  }
  return { state: IDLE_INTERACTION, commands }
}

/** 取消：`Esc` / 手势被抢（双指落下）/ 组件卸载 */
export function cancelInteraction(state: CanvasInteraction): {
  state: CanvasInteraction
  commands: CanvasCommand[]
} {
  const commands: CanvasCommand[] = state.phase === 'dragging' ? [{ type: 'drag-stop' }] : []
  return { state: IDLE_INTERACTION, commands }
}

/**
 * 当前框选矩形（画布坐标，**未归一化**）。
 *
 * ⚠️ 直接用两个**画布坐标**算（`originFlow` / `currentFlow`），
 * 不再从屏幕位移反推 —— 反推要除 zoom，是在给自己挖坑。
 */
export function selectionRect(state: CanvasInteraction, currentFlow?: Point): Rect {
  const end = currentFlow ?? state.currentFlow
  return {
    x: state.originFlow.x,
    y: state.originFlow.y,
    width: end.x - state.originFlow.x,
    height: end.y - state.originFlow.y,
  }
}

/** 框选矩形（已归一化：宽高为正） */
export function normalizedBox(state: CanvasInteraction, currentFlow?: Point): Rect {
  return normalizeRect(selectionRect(state, currentFlow))
}

/** 把可能"反向"的矩形归一（从右下往左上拉也是同一个框） */
export function normalizeRect(rect: Rect): Rect {
  return {
    x: rect.width < 0 ? rect.x + rect.width : rect.x,
    y: rect.height < 0 ? rect.y + rect.height : rect.y,
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  }
}

/** 两个矩形相交（框选判定：**中心在框内**也可以，这里用相交更宽松） */
export function rectsIntersect(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.width < b.x ||
    b.x + b.width < a.x ||
    a.y + a.height < b.y ||
    b.y + b.height < a.y
  )
}

/** 点是否在矩形里 */
export function rectContainsPoint(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  )
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
